/**
 * Country Model - Représente un pays avec ses informations
 * @class Country
 */
export class Country {
    /**
     * Crée une instance de Country
     * @param {Object} data - Données du pays
     */
    constructor(data = {}) {
        this._name = data.name || '';
        this._code = data.code || '';
        this._capital = data.capital || '';
        this._region = data.region || '';
        this._population = data.population || 0;
        this._flag = data.flag || '';
        this._currencies = data.currencies || [];
        this._latlng = data.latlng || [0, 0];
        this._borders = data.borders || [];
        this._translations = data.translations || {};
    }

    // Getters
    get name() { return this._name; }
    get code() { return this._code; }
    get capital() { return this._capital; }
    get region() { return this._region; }
    get population() { return this._population; }
    get flag() { return this._flag; }
    get currencies() { return this._currencies; }
    get latlng() { return this._latlng; }
    get latitude() { return this._latlng[0]; }
    get longitude() { return this._latlng[1]; }
    get borders() { return this._borders; }
    get translations() { return this._translations; }

    // Setters
    set name(value) { this._name = value; }
    set code(value) { this._code = value; }
    set capital(value) { this._capital = value; }
    set region(value) { this._region = value; }
    set population(value) { this._population = value; }
    set flag(value) { this._flag = value; }
    set currencies(value) { this._currencies = value; }
    set latlng(value) { this._latlng = value; }
    set borders(value) { this._borders = value; }
    set translations(value) { this._translations = value; }

    /**
     * Retourne le nom du pays dans la langue demandée
     * @param {string} lang - Code langue (fr, en, etc.)
     * @returns {string} Nom traduit ou nom par défaut
     */
    getName(lang = 'fr') {
        const langMap = {
            'fr': 'fra',
            'es': 'spa',
            'de': 'deu',
            'it': 'ita',
            'pt': 'por',
            'nl': 'nld',
            'ja': 'jpn'
        };

        // En anglais, on utilise le nom par défaut (qui est souvent en anglais via RestCountries "common")
        if (lang === 'en') {
             return this._name;
        }

        const lookupKey = langMap[lang] || lang;

        // Pour les autres langues
        if (this._translations && this._translations[lookupKey] && this._translations[lookupKey].common) {
            return this._translations[lookupKey].common;
        }
        
        return this._name;
    }

    /**
     * Retourne la devise principale du pays
     * @returns {Object|null} Devise principale ou null
     */
    getPrimaryCurrency() {
        return this._currencies.length > 0 ? this._currencies[0] : null;
    }

    /**
     * Formate la population pour l'affichage
     * @returns {string} Population formatée
     */
    getFormattedPopulation() {
        return new Intl.NumberFormat('fr-FR').format(this._population);
    }

    /**
     * Crée une instance Country à partir des données de l'API RestCountries
     * @param {Object} apiData - Données brutes de l'API
     * @returns {Country} Instance de Country
     */
    static fromApiData(apiData) {
        const currencies = [];
        
        if (apiData.currencies) {
            for (const [code, details] of Object.entries(apiData.currencies)) {
                currencies.push({
                    code: code,
                    name: details.name,
                    symbol: details.symbol || code
                });
            }
        }

        return new Country({
            name: apiData.name?.common || apiData.name || '',
            code: apiData.cca2 || apiData.alpha2Code || '',
            capital: Array.isArray(apiData.capital) ? apiData.capital[0] : apiData.capital || '',
            region: apiData.region || '',
            population: apiData.population || 0,
            flag: apiData.flags?.svg || apiData.flags?.png || '',
            currencies: currencies,
            latlng: apiData.latlng || [0, 0],
            borders: apiData.borders || [],
            translations: apiData.translations || {}
        });
    }

    /**
     * Convertit l'instance en objet JSON
     * @returns {Object} Représentation JSON
     */
    toJSON() {
        return {
            name: this._name,
            code: this._code,
            capital: this._capital,
            region: this._region,
            population: this._population,
            flag: this._flag,
            currencies: this._currencies,
            latlng: this._latlng,
            borders: this._borders,
            translations: this._translations
        };
    }
}
